/**
 * PdfExportShell — page chrome shared by the PDF export pages: PageHeader +
 * back button, loading / guard states, the full-width mobile tab strip
 * (הגדרות | תצוגה מקדימה), the two-pane layout (sticky sidebar + paper
 * preview) and the sticky footer bar. Pages supply the panes.
 *
 * Styles: pdfExportShell.css (pdx-*). Design: clinikit-design-2026.
 */

import { useState } from "react";
import { ArrowRight, SlidersHorizontal, Eye } from "@phosphor-icons/react";
import { PageHeader } from "../shared";
import ContentLoader from "../shared/ContentLoader";
import "../../styles/formDocument.css";
import "../dashboard/reports/patientReports/PatientReportWizard.css"; // prw-btn + PdfPreviewModal styles
import "./pdfExportShell.css";

export function PdfExportGuard({ Icon, title, text, onBack, backLabel = "חזרה" }) {
  return (
    <div className="pdx-guard">
      <span className="pdx-guard-icon">
        <Icon size={22} weight="duotone" />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
      <button type="button" className="prw-btn ghost" onClick={onBack}>
        <ArrowRight size={15} weight="bold" />
        {backLabel}
      </button>
    </div>
  );
}

function PdfExportShell({
  icon,
  title,
  subtitle,
  onBack,
  backLabel = "חזרה",
  loading,
  loadingMessage = "טוען...",
  guard, // { Icon, title, text } | null
  sidebar,
  preview,
  previewNote = "תצוגה מקדימה — העימוד הסופי לעמודים נקבע בקובץ ה-PDF ועשוי להשתנות מעט.",
  footer,
  children, // modals etc.
  className = "",
}) {
  const [mobileTab, setMobileTab] = useState("settings");

  return (
    <div className={`pdx-page ${className}`}>
      <PageHeader
        icon={icon}
        title={title}
        subtitle={subtitle}
        actions={
          onBack ? (
            <button
              type="button"
              className="pdx-back-btn"
              onClick={onBack}
              aria-label={backLabel}
              title={backLabel}
            >
              <ArrowRight size={18} weight="bold" />
              <span>{backLabel}</span>
            </button>
          ) : null
        }
      />

      {loading ? (
        <ContentLoader message={loadingMessage} />
      ) : guard ? (
        <PdfExportGuard
          Icon={guard.Icon}
          title={guard.title}
          text={guard.text}
          onBack={onBack}
          backLabel={guard.backLabel || backLabel}
        />
      ) : (
        <>
          <div className="pdx-mobile-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "settings"}
              className={`pdx-mobile-tab ${mobileTab === "settings" ? "active" : ""}`}
              onClick={() => setMobileTab("settings")}
            >
              <SlidersHorizontal size={15} weight={mobileTab === "settings" ? "fill" : "regular"} />
              הגדרות
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileTab === "preview"}
              className={`pdx-mobile-tab ${mobileTab === "preview" ? "active" : ""}`}
              onClick={() => setMobileTab("preview")}
            >
              <Eye size={15} weight={mobileTab === "preview" ? "fill" : "regular"} />
              תצוגה מקדימה
            </button>
          </div>

          <div className={`pdx-layout mobile-${mobileTab}`}>
            <aside className="pdx-side">{sidebar}</aside>
            <section className="pdx-preview-pane">
              {previewNote && <div className="pdx-preview-note">{previewNote}</div>}
              <div className="pdx-preview-scroll">{preview}</div>
            </section>
          </div>

          {footer && <div className="pdx-footer">{footer}</div>}
        </>
      )}

      {children}
    </div>
  );
}

export default PdfExportShell;
